import React from "react";
import { ArrowRight, PlayCircle } from "lucide-react";
import landing from "@/content/landing";
import landingMedia from "@/content/landing-media";

/**
 * Landingpage-Sektions-Wrapper mit konsistentem Container + Spacing.
 */
export function Section({ id, className = "", children, ...rest }) {
  return (
    <section id={id} className={`relative px-5 py-20 sm:py-24 lg:px-8 lg:py-32 ${className}`} {...rest}>
      <div className="relative mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

export default function HeroSection() {
  const t = landing.hero;
  const hasVideo = Boolean(landingMedia.heroVideo);
  const poster = landingMedia.heroPoster || undefined;

  return (
    <Section id="hero" className="pt-10 sm:pt-14 lg:pt-20 relative overflow-hidden" data-testid="landing-hero">
      {/* Warme Gold-Ambience oben rechts + unten links */}
      <div className="ag-gold-ambient" style={{ width: 520, height: 520, top: -160, right: -140 }} aria-hidden="true" />
      <div className="ag-gold-ambient" style={{ width: 380, height: 380, bottom: -180, left: -120, opacity: 0.22 }} aria-hidden="true" />

      <div className="relative grid gap-10 lg:grid-cols-12 lg:gap-16 lg:items-center">
        <div className="lg:col-span-6">
          <div className="ag-overline mb-6">{t.overline}</div>
          <h1 className="font-serif text-4xl leading-[1.08] text-ag-ink sm:text-5xl lg:text-6xl">
            {t.headline}
          </h1>
          <p className="mt-6 font-serif text-xl italic text-slate-600 sm:text-2xl">
            {t.subline}
          </p>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-700 sm:text-[17px]">
            {t.description}
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <a
              href={t.ctaPrimaryHref}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-ag-ink px-7 text-sm font-medium text-white shadow-sm transition hover:bg-ag-ink/90 hover:shadow-md"
              data-testid="landing-hero-cta-primary"
            >
              {t.ctaPrimary}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={t.ctaSecondaryHref}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-ag-ink/70 px-6 text-sm font-medium text-ag-ink transition hover:bg-ag-ink/5"
              data-testid="landing-hero-cta-secondary"
            >
              <PlayCircle className="h-4 w-4" />
              {t.ctaSecondary}
            </a>
          </div>

          <div className="mt-10 flex items-center gap-4">
            <div className="h-px w-10 bg-amber-500/70" aria-hidden="true" />
            <p className="text-sm italic text-slate-500">{t.claim}</p>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div
            id="hero-video"
            className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 shadow-2xl"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="landing-hero-media"
          >
            {/* Gold-Glow innerhalb Karte */}
            <div className="pointer-events-none absolute -top-10 -right-10 h-52 w-52 rounded-full bg-amber-400/20 blur-3xl" aria-hidden="true" />
            {hasVideo ? (
              <video
                className="h-full w-full object-cover"
                src={landingMedia.heroVideo}
                poster={poster}
                muted
                loop
                playsInline
                preload="metadata"
                controls
                aria-label="ALLES GEREGELT · Vorschau-Video"
              />
            ) : (
              <div className="relative flex h-full w-full items-center justify-center p-8 text-center">
                <div className="text-white/80">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-200/30 bg-white/5 backdrop-blur-sm">
                    <PlayCircle className="h-8 w-8 text-amber-200/90" strokeWidth={1.2} aria-hidden="true" />
                  </div>
                  <div className="mt-5 text-sm text-white/80">
                    {t.videoPlaceholder}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}
